import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Loader2, RefreshCw } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import DeploymentCard from '../../components/strategy-lab/DeploymentCard';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { fmtSignedUsd, pnlColorClass } from '../../components/strategy-lab/format';

const DEPLOYMENTS_POLL_MS = 60000;

const DeploymentsPage: React.FC = () => {
  const navigate = useNavigate();
  const deployments = useStrategyLabStore((s) => s.deployments);
  const loading = useStrategyLabStore((s) => s.deploymentsLoading);
  const error = useStrategyLabStore((s) => s.deploymentsError);
  const fetchDeployments = useStrategyLabStore((s) => s.fetchDeployments);

  useEffect(() => {
    fetchDeployments();
    const t = setInterval(fetchDeployments, DEPLOYMENTS_POLL_MS);
    return () => clearInterval(t);
  }, [fetchDeployments]);

  const totalPnl = deployments.reduce((acc, d) => acc + (d.pnl_today || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" /> Deployments
          </h2>
          {deployments.length > 0 && (
            <span className="text-sm text-muted-foreground">
              Net today:{' '}
              <span className={`font-semibold ${pnlColorClass(totalPnl)}`}>
                {fmtSignedUsd(totalPnl)}
              </span>
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchDeployments()} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </Button>
      </div>

      {error && <p className="text-sm text-rose-500">{error}</p>}

      {loading && deployments.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading deployments…
        </div>
      ) : deployments.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-muted-foreground text-sm space-y-3">
            <p>No active deployments. Deploy a strategy with a daily budget to start trading.</p>
            <Button variant="outline" size="sm" onClick={() => navigate('/strategy-lab/strategies')}>
              <Rocket className="w-4 h-4" /> Go to strategies
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {deployments.map((d) => (
            <DeploymentCard key={d.id} deployment={d} />
          ))}
        </div>
      )}
    </div>
  );
};

export default DeploymentsPage;
